import React from 'react';

import styled from 'styled-components';

import { MdCloudUpload, MdVisibility, MdDelete } from 'react-icons/md';
import { HiDownload } from 'react-icons/hi';

type Props = {
  onClick?: (e: any) => void;
  onChange?: (e: any) => void;
  onDownload?: (e: any) => void;
  onDelete?: (e: any) => void;
  type?: 'button' | 'reset' | 'submit' | undefined;
  label?: string | JSX.Element;
  form?: string | undefined;
  disabled?: boolean;
  component?: any;
  upload?: boolean;
  icon?: JSX.Element;
  iconPosition?: 'left' | 'right';
  width?: string;
  variant?: 'primary' | 'white' | 'green' | 'red';
  margin?: string;
  index?: number;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'>;

export const Button = ({
  onClick,
  onChange,
  onDownload,
  onDelete,
  type = 'button',
  form,
  label,
  disabled = false,
  upload = false,
  icon,
  iconPosition = 'right',
  width,
  variant = 'primary',
  margin,
  index,
  ...props
}: Props): any => {
  if (upload)
    return (
      <UploadButton width={width} margin={margin}>
        <div>
          <Icon
            htmlFor={`file-upload-${index}`}
            $disabled={!!label || disabled}
          >
            <input
              disabled={!!label || disabled}
              id={`file-upload-${index}`}
              hidden
              type="file"
              onChange={onChange}
              accept=".jpg, .jpeg, .png, .pdf, .docx, .doc, .csv, .gif, .ppt, .pptx, .txt, .xlsx, .xls, .zip"
            />
            <MdCloudUpload />
          </Icon>
          {label}
        </div>

        {label && (
          <div>
            <Icon onClick={onDownload}>
              {label.toString().endsWith('.pdf') ? (
                <MdVisibility />
              ) : (
                <HiDownload />
              )}
            </Icon>
            {!disabled && (
              <Icon onClick={onDelete}>
                <MdDelete />
              </Icon>
            )}
          </div>
        )}
      </UploadButton>
    );
  else
    return (
      <StyledButton
        {...props}
        type={type}
        form={form}
        onClick={onClick}
        disabled={disabled}
        variant={variant}
        width={width}
        margin={margin}
      >
        {icon && iconPosition === 'left' && (
          <StyledIcon variant={variant} position={iconPosition}>
            {icon}
          </StyledIcon>
        )}
        {label}
        {icon && iconPosition === 'right' && (
          <StyledIcon variant={variant} position={iconPosition}>
            {icon}
          </StyledIcon>
        )}
      </StyledButton>
    );
};

const StyledIcon = styled.span<{ variant: string; position: string }>`
  margin-left: ${({ position }) => (position === 'right' ? '0.5rem' : '0')};
  margin-right: ${({ position }) => (position === 'left' ? '0.5rem' : '0')};
  font-size: 1.3rem;
  color: ${({ variant }) => getLabelColor({ variant })};
`;

const StyledButton = styled.button<{
  width?: string;
  variant: string;
  margin?: string;
}>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 0.6rem 1rem;
  font-weight: 600;
  font-size: 0.9rem;
  height: max-content;
  background-color: ${({ variant }) => getBackgroundColor({ variant })};
  border: 2px solid ${({ variant }) => getBorderColor({ variant })};
  color: ${({ variant }) => getLabelColor({ variant })};
  border-radius: 0.375rem;
  box-shadow: inset 0 2px 4px 0 rgb(0 0 0 / 0.05);
  transition: all 0.1s ease-in-out;
  letter-spacing: 1.2px;
  ${({ width }) => width && `width: ${width}`}px;
  ${({ margin }) => margin && `margin: ${margin}`};
  &:hover {
    background-color: ${({ variant }) => getHoveredColor({ variant })};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
  &:focus: {
    outline: none;
  }
`;

const UploadButton = styled.div<{
  width?: string;
  margin?: string;
}>`
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  padding: 0rem 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
  background-color: #f1f1f1;
  border: 2px solid #154077;
  color: #154077;
  border-radius: 1rem;
  box-shadow: 0 0.1rem 0.2rem 0.05rem rgba(0, 0, 0, 0.25);
  transition: all 0.1s ease-in-out;
  letter-spacing: 1.2px;
  ${({ width }) => width && `width: ${width}`}px;
  ${({ margin }) => margin && `margin: ${margin}`};

  & > div {
    display: flex;
    align-items: center;
  }

  &:focus: {
    outline: none;
  }
`;

const Icon = styled.label<{ $disabled?: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  margin-right: 0.5rem;
  & > svg {
    color: #154077;
    font-size: 2rem;

    &:hover {
      transform: scale(${({ $disabled }) => ($disabled ? 1 : 1.2)});
      transition: transform 0.2s ease-in-out;
    }
    @media (max-width: 640px) {
      font-size: 1.5rem;
    }
  }
`;

const getBackgroundColor = ({ variant }: { variant: string }): string => {
  switch (variant) {
    case 'primary':
      return '#154077';
    case 'white':
      return '#f1f1f1';
    case 'green':
      return '#C6F6D5';
    case 'red':
      return '#FEB2B2';
    default:
      return '#154077';
  }
};

const getHoveredColor = ({ variant }: { variant: string }): string => {
  switch (variant) {
    case 'primary':
      return '#113665';
    case 'white':
      return '#e3e3e3';
    case 'green':
      return '#70f3cb';
    case 'red':
      return '#FF9A9A';
    default:
      return '#113665';
  }
};

const getBorderColor = ({ variant }: { variant: string }): string => {
  switch (variant) {
    case 'primary':
      return '#aabb00';
    case 'white':
      return '#d1d5db';
    case 'green':
      return '#18DBA0';
    case 'red':
      return '#F6404B';
    default:
      return '#aabb00';
  }
};

const getLabelColor = ({ variant }: { variant: string }): string => {
  switch (variant) {
    case 'primary':
      return 'white';
    case 'white':
      return '#154077';
    case 'green':
      return '#047B57';
    case 'red':
      return '#992424';
    default:
      return 'white';
  }
};
